/**
 * Copyright by:
 * by ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */

import { GanttChartData, GanttSegment } from '@/app/shared/gantt-chart/gantt-chart.models';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer, MatSidenav } from '@angular/material/sidenav';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TaskManagerComponent } from '../../task-home/task/task-manager/task-manager.component';
import { Logger, LogService } from '@epg/angular-base-lib';
import {
  ButtonType,
  ConfirmDeleteDialogData,
  ConfirmDialogService,
  EpgTableComponent,
  EpgTablePageEvent
} from '@epg/ng-components';
import { WorkflowValidationService } from '@epg/workflow-designer-lib';
import {
  faArrowRight,
  faArrowsRepeat,
  faBan,
  faCalendarRange,
  faCheck,
  faCircleDashed,
  faClipboardListCheck,
  faCopy,
  faSearchPlus,
  faSpinnerThird,
  faStar,
  faTrash,
  faUpload
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faDiamondExclamation,
  faListCheck,
  faPenRuler,
  faSortDown,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import * as dateFns from 'date-fns';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { opencanDeactivateDialog, toTitleCase } from '../../../../app/shared/functions';
import { CanComponentDeactivate } from '../../../../app/shared/interface/can-component-deactivate.interface';
import {
  OneAppActionsMenuConfig,
  OneAppActionsMenuItemConfig,
  OneAppButtonConfig
} from '../../../../app/shared/oneapp-header/oneapp-header.component';
import { TaskAssignmentType } from '../../../../enums/task-assignment-type.enum';
import { TaskStatus } from '../../../../enums/task-status.enum';
import { RecurringModel, ScheduledTaskModel } from '../../../../models/scheduled-task.model';
import { TaskModel } from '../../../../models/task.model';
import { WorkflowModel } from '../../../../models/workflow.model';
import {
  CustomDateFormatService,
  DateFormat
} from '../../../../services/custom-date-format/custom-date-format.service';
import { EmployeeService } from '../../../../services/employee.service';
import { RecurringConfig, ScheduledTaskService } from '../../../../services/scheduled-task.service';
import { TaskService } from '../../../../services/task.service';
import { WorkflowService } from '../../../../services/workflow.service';
import { ChipsElementModel } from '../../../shared/chips-autocomplete/chips-autocomplete.component';
import {
  DateFilterValue,
  DateRangePickerComponent,
  TaskDateFilterPresets
} from '../../../shared/date-range-picker/date-range-picker.component';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { GalleryFilter } from '../../../shared/oneapp-task-image-grid/oneapp-task-image-grid.component';
const EDIT_CALL_BACK_IDENTIFIER = 'edit';
const CREATE_TASK_CALL_BACK_IDENTIFIER = 'create task';
const DUPLICATE_CALL_BACK_IDENTIFIER = 'duplicate';
const PUBLISH_CALL_BACK_IDENTIFIER = 'publish';
const UNPUBLISH_CALL_BACK_IDENTIFIER = 'unpublish';
const DELETE_CALL_BACK_IDENTIFIER = 'delete';

@Component({
  selector: 'app-workflow-detail',
  templateUrl: './workflow-detail.component.html',
  styleUrls: ['./workflow-detail.component.scss'],
  standalone: false
})
export class WorkflowDetailComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('taskDrawer') taskSidenav?: MatSidenav;
  @ViewChild('taskManagerDrawer') taskManagerSidenav?: MatDrawer;
  @ViewChild('taskManagerComponent') taskManagerComponent?: TaskManagerComponent;
  @ViewChild('taskTable') taskTable!: EpgTableComponent;
  @ViewChild('scheduledTaskTable') scheduledTaskTable!: EpgTableComponent;

  private readonly destroy$ = new Subject<void>();

  selectedTaskId: string | null = null;

  private readonly cancelSubject = new Subject<void>();
  @ViewChild('workflowDuplicationTemplate', { static: true })
  workflowDuplicationTemplate!: TemplateRef<any>;
  dialogTitle = $localize`:@@duplicateWorkflow:Duplicate Workflow`;

  @ViewChild('workflowUnpublishTemplate', { static: true })
  workflowUnpublishTemplate!: TemplateRef<any>;

  readonly RecurringConfig = RecurringConfig;

  readonly DETAILS_TAB = 0;
  readonly ACTIONS_TAB = 1;
  readonly GALLERY_TAB = 2;

  readonly faSearchPlus = faSearchPlus;
  readonly faSpinnerThird = faSpinnerThird;
  readonly faStar = faStar;
  readonly faArrowRight = faArrowRight;
  readonly faCheck = faCheck;
  readonly faCircleCheck = faCircleCheck;
  readonly faCircle = faCircle;
  readonly faCircleExclamation = faCircleExclamation;
  readonly faDiamondExclamation = faDiamondExclamation;
  readonly faCircleDashed = faCircleDashed;
  readonly faArrowsRepeat = faArrowsRepeat;
  readonly faCalendarRange = faCalendarRange;
  readonly faSortDown = faSortDown;
  readonly faXmark = faXmark;

  readonly EXECUTED_BY_TEXT = $localize`:@@executedByDropDown:Executed by`;
  readonly SELECT_EMPLOYEES_PLACEHOLDER = $localize`:@@selectEmployees:Select employees`;

  loadingWorkflow = false;
  loadingTasks = false;
  currentPublishedWorkflowId = '';
  currentDesignerWorkflowId = '';
  currentCombinedWorkflowId = '';
  private workflowInitializedFromState = false;
  showJson = true;
  showStartingFrom = true;
  geoCodingEnabled = $localize`:@@disabled:Disabled`;
  saveTimeStamps = $localize`:@@disabled:Disabled`;

  workflow: WorkflowModel = this.initializeWorkflow();
  unsavedChangesExist = false;

  readonly TaskStatus = TaskStatus;

  customButtonConfig: OneAppButtonConfig = {
    id: 'edit_workflow',
    text: $localize`:edit@@edit:edit`,
    callBackIdentifier: EDIT_CALL_BACK_IDENTIFIER,
    disabled: false,
    icon: faPenRuler,
    requiredPermissions: []
  };

  private _customButtonConfig2: OneAppButtonConfig = {
    id: 'create_task',
    text: $localize`:@@createTask:Create task`,
    callBackIdentifier: CREATE_TASK_CALL_BACK_IDENTIFIER,
    disabled: false,
    icon: faListCheck,
    type: ButtonType.PRIMARY,
    requiredPermissions: []
  };

  get customButtonConfig2(): OneAppButtonConfig | undefined {
    if (!this.isWorkflowFree(this.workflow)) {
      return undefined;
    }
    return this._customButtonConfig2;
  }

  private isWorkflowFree(workflow: WorkflowModel): boolean {
    if (workflow?.executableFromMobile !== undefined) {
      return workflow.executableFromMobile === true;
    }
    return workflow?.input === undefined || Object.keys(workflow.input).length === 0;
  }

  actionsMenu: OneAppActionsMenuItemConfig[] = [
    {
      id: 'duplicate_workflow',
      text: $localize`:@@duplicate:Duplicate`,
      callBackIdentifier: DUPLICATE_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faCopy
    },
    {
      id: 'publish_workflow',
      text: $localize`:@@publish:Publish`,
      callBackIdentifier: PUBLISH_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faUpload
    },
    {
      id: 'unpublish_workflow',
      text: $localize`:@@unpublish:Unpublish`,
      callBackIdentifier: UNPUBLISH_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faBan
    },
    {
      id: 'delete_workflow',
      text: $localize`:@@delete:Delete`,
      callBackIdentifier: DELETE_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faTrash,
      color: '#DE2839'
    }
  ];
  actionsMenuConfig: OneAppActionsMenuConfig = {
    id: 'workflow_menu',
    disabled: false,
    actionsMenu: this.actionsMenu
  };
  private readonly initialDefaultDates = DateRangePickerComponent.computeDatesForPreset(
    TaskDateFilterPresets.lastSevenDays
  )!;

  galleryFilter: GalleryFilter = {
    from: this.initialDefaultDates.startDate,
    to: this.initialDefaultDates.endDate,
    workflowId: '',
    employeeId: []
  };

  workflowDesignerVisible = false;

  readonly taskColumns = [
    {
      field: 'recurring',
      header: '',
      customTemplate: true
    },
    {
      field: 'name',
      header: $localize`:@@taskNameLabel:Task name`
    },
    {
      field: 'assignedTo',
      header: $localize`:@@assignedTo:Assigned to`,
      customTemplate: true
    },
    {
      field: 'status',
      header: $localize`:@@status:Status`,
      customTemplate: true
    },
    {
      field: 'scheduled_at',
      header: $localize`:@@scheduledAt:Scheduled for`,
      customTemplate: true
    },
    {
      field: 'time_tracking',
      header: $localize`:@@duration:Duration`,
      customTemplate: true
    },
    {
      field: 'startedAt',
      header: $localize`:@@startedAt:Started at`,
      customTemplate: true
    },
    {
      field: 'endedAt',
      header: $localize`:@@endedAt:Ended at`,
      customTemplate: true
    },
    {
      field: 'incidents',
      header: $localize`:@@incidents:Incidents`,
      customTemplate: true
    }
  ];

  taskTableModels: {
    taskId: string;
    recurring?: RecurringModel;
    name: string;
    assignedTo: string[];
    assignedToEmployeeOrEmployeegroup: TaskAssignmentType;
    status: TaskStatus;
    scheduled_at?: string;
    time_tracking?: {
      name: string;
      start: Date;
      employee_id: string;
    }[];
    startedAt?: Date | undefined;
    endedAt?: Date | undefined;
    updatedAt: string;
    incidents?: object[];
  }[] = [];
  taskTableTotalCount = 0;
  totalTasksForWorkflow: number | null = null;
  private totalTasksForWorkflowFetched = false;

  currentDateFilter: DateFilterValue = {
    startDate: this.initialDefaultDates.startDate,
    endDate: this.initialDefaultDates.endDate,
    preset: TaskDateFilterPresets.lastSevenDays
  };

  readonly scheduledTaskColumns = [
    {
      field: 'taskName',
      header: $localize`:@@taskNameLabel:Task name`
    },
    {
      field: 'assignedTo',
      header: $localize`:@@assignedTo:Assigned to`,
      customTemplate: true
    },
    {
      field: 'recurring',
      header: $localize`:@@RepeatLabel:Repeat`,
      customTemplate: true
    },
    {
      field: 'scheduledAt',
      header: $localize`:@@scheduling:Scheduling`,
      customTemplate: true
    }
  ];

  scheduledTasks$ = this.scheduledTaskService.filteredScheduledTasks$;
  currentScheduledTasks: ScheduledTaskModel[] = [];
  private scheduledTasksSubscribed = false;
  private lastScheduledTasks: ScheduledTaskModel[] = [];
  is_published = true;
  duplicatedWorkflowName: string = '';
  private readonly log: Logger;

  selectedTabIndex = 0;
  loadedTabs = new Set<number>([0]);

  availableEmployees: ChipsElementModel[] = [];
  selectedEmployeeIds: string[] = [];
  private suppressEmployeeQuerySync = false;

  galleryLoaded = false;
  galleryHasImages = false;

  isLoadingAnalytics = false;
  analyticsError: string | null = null;
  ganttData: GanttChartData | null = null;
  selectedStatistic: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private cdr: ChangeDetectorRef,
    public customDateFormatService: CustomDateFormatService,
    private taskService: TaskService,
    private elRef: ElementRef,
    private employeeService: EmployeeService,
    private readonly dialogService: ConfirmDialogService,
    private dialog: MatDialog,
    private logService: LogService,
    private workflowValidationService: WorkflowValidationService,
    private scheduledTaskService: ScheduledTaskService
  ) {
    this.log = logService.getLogger(this);
  }

  async ngOnInit(): Promise<void> {
    this.employeeService.getEmployeeGroups();

    const navigation = this.router.getCurrentNavigation();
    const stateWorkflow =
      (navigation?.extras?.state as any)?.workflow ?? (history.state as any)?.workflow;

    if (stateWorkflow && stateWorkflow.combinedId) {
      this.workflow = stateWorkflow as WorkflowModel;
      this.currentCombinedWorkflowId = stateWorkflow.combinedId;
      this.splitCombinedId(this.currentCombinedWorkflowId);
      this.is_published = !!(stateWorkflow as WorkflowModel).is_published;
      this.updateWorkflowFlagsAndLayout();
      this.workflowInitializedFromState = true;
    }

    this.subscribeToRouteParams();
    this.subscribeToEmployeeQueryParams();
    this.subscribeToDateFilterQueryParams();

    this.taskService.taskDeleted.pipe(takeUntil(this.destroy$)).subscribe((deletedTask) => {
      if (this.taskTableModels.some((taskModel) => taskModel.taskId == deletedTask.taskId)) {
        this.taskTable.pageEvent.emit({
          firstIndex: this.taskTable.firstIndex(),
          rows: this.taskTable.rowsPerPage()
        });
      }
      if (this.selectedTaskId == deletedTask.taskId) {
        this.taskTable.rowSelection.set(null);
      }
    });

    this.taskService.newTaskCreated.pipe(takeUntil(this.destroy$)).subscribe((newTask) => {
      if (this.taskTableModels.length === 0) {
        this.fetchTasks(0, 10);
      } else {
        this.taskTable.pageHandler(1);
      }

      if (newTask.scheduled_at) {
        this.scheduledTaskService.addScheduleTaskEvent.next({} as any); // Trigger fetch of scheduled tasks
      }

      if (this.selectedTaskId != null) {
        this.taskTable.rowSelection.set(null);
      }
    });

    this.loadAvailableEmployees();
  }

  ngAfterViewInit(): void {
    this.setDynamicHeight();

    if (this.taskSidenav) {
      this.taskSidenav.closedStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.selectedTaskId) {
          this.dismissSlideIn();
        }
      });
    }

    this.taskManagerSidenav?.closedStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.workflowDesignerVisible = false;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.selectedTaskId && !this.taskManagerSidenav?.opened) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    // If the click target was removed from the DOM (e.g. by ngx-material-timepicker
    // destroying its overlay before the event finishes bubbling), ignore it.
    if (!target.isConnected) {
      return;
    }

    if (target.closest('mat-drawer')) {
      return;
    }

    if (target.closest('epg-table')) {
      return;
    }

    if (
      target.closest('ngx-material-timepicker-container') ||
      target.closest('.timepicker-overlay') ||
      target.closest('.timepicker-backdrop-overlay')
    ) {
      return;
    }

    // Also guard against the timepicker still being open (e.g. during close animation)
    if (document.querySelector('ngx-material-timepicker-container')) {
      return;
    }

    if (
      target.closest('.cdk-overlay-container') ||
      target.closest('.cdk-overlay-pane') ||
      target.closest('.mat-select-panel')
    ) {
      return;
    }

    if (target.closest('#create_task')) {
      return;
    }

    this.dismissSlideIn();
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.setDynamicHeight();
  }

  private subscribeToRouteParams(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      if (params['workflowId']) {
        this.taskSidenav?.close();
        this.taskManagerSidenav?.close();
        this.selectedTaskId = null;
        const stateWorkflow = (history.state as any)?.workflow;

        if (stateWorkflow && stateWorkflow.combinedId === params['workflowId']) {
          // Use workflow passed via navigation state (from the list component)
          this.workflow = stateWorkflow as WorkflowModel;
          this.currentCombinedWorkflowId = stateWorkflow.combinedId;
          this.splitCombinedId(this.currentCombinedWorkflowId);
          this.is_published = !!(stateWorkflow as WorkflowModel).is_published;
          this.workflowInitializedFromState = true;
        } else {
          // Fallback: no state available, rely on IDs and service loading
          this.currentCombinedWorkflowId = params['workflowId'];
          this.splitCombinedId(this.currentCombinedWorkflowId);
          this.workflowInitializedFromState = false;
        }

        this.totalTasksForWorkflow = this.currentPublishedWorkflowId ? null : 0;
        this.totalTasksForWorkflowFetched = false;
        this.selectedStatistic = null;

        if (!this.workflowInitializedFromState) {
          await this.loadWorkflow();
        } else if (this.currentPublishedWorkflowId) {
          this.loadingTasks = true;
          this.cdr.detectChanges();
          this.fetchTasks(0, 10);
          this.loadAnalyticsData();
        } else {
          // No published workflow part: clear tasks and table state
          this.taskTableModels = [];
          this.taskTableTotalCount = 0;
          this.loadingTasks = false;
          this.cdr.detectChanges();
          this.loadAnalyticsData();
        }
        this.selectedTabIndex = 0;

        this.subscribeToScheduledTasks();

        this.updateWorkflowFlagsAndLayout();
      }
    });
  }

  private subscribeToEmployeeQueryParams(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
      if (this.suppressEmployeeQuerySync) {
        this.suppressEmployeeQuerySync = false;
        return;
      }

      const employeesParam = paramMap.get('employees');
      const parsedIds = employeesParam
        ? employeesParam
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0)
        : [];

      this.applyEmployeeSelection(parsedIds, 'url');
    });
  }

  private subscribeToDateFilterQueryParams(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
      const dateFilterParam = paramMap.get('date-filter');
      const dateFilterPresetParam = paramMap.get('date-filter-preset');

      if (dateFilterParam) {
        // Skip self-echo: if URL param matches what we'd format from current state
        if (
          this.currentDateFilter.preset === TaskDateFilterPresets.custom &&
          dateFilterParam ===
            DateRangePickerComponent.formatDateFilterParam(this.currentDateFilter, false)
        ) {
          return;
        }
        const parsed = DateRangePickerComponent.parseDateFilterParam(dateFilterParam, false);
        if (parsed) {
          this.currentDateFilter = parsed;
          this.onDateFilterInternalChange();
        }
      } else if (dateFilterPresetParam) {
        // Skip self-echo: if URL param matches what we'd format from current state
        if (
          dateFilterPresetParam ===
          DateRangePickerComponent.formatPresetParam(this.currentDateFilter.preset)
        ) {
          return;
        }
        const preset = DateRangePickerComponent.parsePresetParam(dateFilterPresetParam);
        if (preset !== null) {
          const dates = DateRangePickerComponent.computeDatesForPreset(preset);
          if (dates) {
            this.currentDateFilter = {
              startDate: dates.startDate,
              endDate: dates.endDate,
              preset
            };
            this.onDateFilterInternalChange();
          }
        }
      }
    });
  }

  private setDynamicHeight(): void {
    const workflowDetailContainer = this.elRef.nativeElement.querySelector(
      '#workflowDetailContainer'
    );
    const descriptionContainer = this.elRef.nativeElement.querySelector('#descriptionContainer');
    const tabGroupContainer = this.elRef.nativeElement.querySelector('#tabGroupContainer');

    const containerHeight = descriptionContainer?.offsetHeight ?? 0;
    const workflowDetailContainerHeight = workflowDetailContainer?.offsetHeight ?? 0;
    const tabGroupContainerHeight = workflowDetailContainerHeight - containerHeight - 64;

    if (tabGroupContainer) {
      tabGroupContainer.style.height = `${tabGroupContainerHeight}px`;
    }
  }

  private updateWorkflowFlagsAndLayout(): void {
    if (this.workflow.locationTracingEnabled) {
      this.geoCodingEnabled = $localize`:@@enabled:Enabled`;
    } else {
      this.geoCodingEnabled = $localize`:@@disabled:Disabled`;
    }

    if (this.workflow.enableStatesTimestamps) {
      this.saveTimeStamps = $localize`:@@enabled:Enabled`;
    } else {
      this.saveTimeStamps = $localize`:@@disabled:Disabled`;
    }

    this.setDynamicHeight();
  }

  private async loadWorkflow(): Promise<void> {
    this.loadingWorkflow = true;
    // Reset task counters when loading a new workflow
    this.totalTasksForWorkflow = this.currentPublishedWorkflowId ? null : 0;
    this.totalTasksForWorkflowFetched = false;
    this.taskTableTotalCount = 0;
    this.actionsMenuConfig.disabled = true;
    try {
      await this.loadCombinedWorkflow();

      if (this.currentPublishedWorkflowId) {
        this.taskTableModels = [];
        this.is_published = true;

        this.loadingTasks = true;
        await this.fetchTasks(0, 10);
        await this.loadAnalyticsData();
      } else if (this.currentDesignerWorkflowId) {
        this.taskTableModels = [];
        this.is_published = false;
        this.totalTasksForWorkflow = 0;
        // Clear analytics data for unpublished workflows
        this.ganttData = null;
        this.selectedStatistic = null;
      }
    } catch (error) {
      this.log.error('Error loading workflow:', error);
    } finally {
      this.loadingWorkflow = false;
      this.handleDesignerActionsButtons();
      this.cdr.detectChanges();

      this.updateGalleryFilter();
      this.handleDesignerActionsButtons();
    }
  }

  private loadCombinedWorkflow(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.workflowService.getCombinedWorkflowById(this.currentCombinedWorkflowId).subscribe(
        (workflow) => {
          if (workflow) {
            this.workflow = workflow;
            resolve();
          } else {
            this.log.error('Workflow not found');
            reject();
          }
        },
        (error) => {
          this.log.error('Error fetching workflow:', error);
          reject();
        }
      );
    });
  }

  private subscribeToScheduledTasks(): void {
    // Subscribe only once to react to future scheduled task changes
    if (!this.scheduledTasksSubscribed) {
      this.scheduledTasksSubscribed = true;
      this.scheduledTasks$.pipe(takeUntil(this.destroy$)).subscribe((scheduledTasks) => {
        this.lastScheduledTasks = scheduledTasks;
        this.filterScheduledTasksByWorkflow();
      });
    } else {
      // Already subscribed - just re-filter cached data when workflow changes
      this.filterScheduledTasksByWorkflow();
    }
  }

  private filterScheduledTasksByWorkflow(): void {
    this.currentScheduledTasks = this.lastScheduledTasks.filter(
      (scheduledTask) => scheduledTask.workflowName === this.workflow.name
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async fetchTasks(skip: number, limit: number): Promise<void> {
    // Build search criteria with optional status filtering
    const searchCriteria: any = {
      workflow_id: this.workflow._id,
      updatedAt: {
        $gte: dateFns.startOfDay(this.currentDateFilter.startDate),
        $lte: dateFns.endOfDay(this.currentDateFilter.endDate)
      }
    };

    // Add status filtering based on selected statistic
    if (this.selectedStatistic) {
      switch (this.selectedStatistic) {
        case 'finished':
          searchCriteria.status = 'CLOSED';
          break;
        case 'inProgress':
          searchCriteria.status = 'IN_PROGRESS';
          break;
        case 'open':
          searchCriteria.status = 'NEW';
          break;
        case 'overdue':
          // Overdue tasks are open or in-progress tasks past their scheduled date
          searchCriteria.status = { $in: ['NEW', 'IN_PROGRESS'] };
          searchCriteria.scheduled_at = { $lt: new Date() };
          break;
        case 'scheduled':
          // Scheduled tasks have a scheduled_at date in the future
          searchCriteria.scheduled_at = { $gte: new Date() };
          break;
        case 'withIncidents':
          // Tasks with incidents have incidents array with length > 0
          searchCriteria.incidents = { $exists: true, $not: { $size: 0 } };
          break;
        // 'total' case - no additional filtering needed
      }
    }

    if (this.selectedEmployeeIds.length > 0) {
      searchCriteria.executed_by = { $in: this.selectedEmployeeIds };
    }

    try {
      let searchResponse = await this.taskService.searchTask({
        search_criteria: searchCriteria,
        skip: skip,
        limit: limit,
        sort: '-updatedAt',
        select:
          'task_id name assigned_to assigned_to_employee_or_employeegroup status executed_by scheduled_at scheduled_by time_tracking incidents createdAt updatedAt'
      });

      this.taskTableTotalCount = (searchResponse.headers as any)['X-Total-Count'] ?? 0;

      if (this.taskTableTotalCount == 0) {
        await this.fetchTotalTasksForWorkflow();
      }

      this.taskTableModels = searchResponse.body.map((task) => {
        const recurring = task.scheduled_by
          ? this.currentScheduledTasks.find(
              (scheduledTask) => scheduledTask.taskId == task.scheduled_by
            )?.recurring
          : undefined;

        return {
          taskId: task.taskId,
          recurring: recurring,
          name: task.name,
          assignedTo: task.assignedTo,
          assignedToEmployeeOrEmployeegroup: task.assignedToEmployeeOrEmployeegroup,
          status: task.status,
          scheduled_at: task.scheduled_at,
          time_tracking: task.time_tracking,
          startedAt: task.time_tracking?.find((checkpoint) => checkpoint.name === 'start')?.start,
          endedAt: task.time_tracking?.find((checkpoint) => checkpoint.name === 'end')?.start,
          updatedAt: task.updatedAt,
          incidents: task.incidents
        };
      });
    } catch (error) {
      this.log.error('Error fetching tasks:', error);
      this.taskTableModels = [];
      this.taskTableTotalCount = 0;
    } finally {
      this.loadingTasks = false;
      this.cdr.detectChanges();
    }
  }

  async getGanttChartData(): Promise<GanttChartData> {
    try {
      const workflowId = this.currentPublishedWorkflowId;

      if (!workflowId) {
        throw new Error('No workflow ID available for analytics');
      }

      const requestBody: any = {
        from: dateFns.startOfDay(this.currentDateFilter.startDate).toISOString(),
        to: dateFns.endOfDay(this.currentDateFilter.endDate).toISOString(),
        select: [
          'total_tasks',
          'new_tasks',
          'finished_tasks',
          'in_progress_tasks',
          'overdue_new_tasks',
          'overdue_in_progress_tasks',
          'scheduled_tasks',
          'tasks_with_incidents'
        ]
      };

      if (this.selectedEmployeeIds.length > 0) {
        requestBody.executedBy = [...this.selectedEmployeeIds];
      }

      const statistics = await this.workflowService.getWorkflowStatistics(workflowId, requestBody);

      const countData = statistics.count || statistics;
      const ganttData = {
        total: countData.total_tasks || 0,
        finished: countData.finished_tasks || 0,
        inProgress: countData.in_progress_tasks || 0,
        open: countData.new_tasks || 0,
        overdue: (countData.overdue_new_tasks || 0) + (countData.overdue_in_progress_tasks || 0),
        overdueNew: countData.overdue_new_tasks || 0,
        overdueInProgress: countData.overdue_in_progress_tasks || 0,
        scheduled: countData.scheduled_tasks || 0,
        withIncidents: countData.tasks_with_incidents || 0
      };

      return ganttData;
    } catch (error) {
      this.log.error('Failed to load analytics data:', error);
      throw error;
    }
  }

  private createDateRange(months: number = 4): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - months);

    return {
      from: from.toISOString(),
      to: to.toISOString()
    };
  }

  private async fetchTotalTasksForWorkflow(): Promise<void> {
    if (this.totalTasksForWorkflowFetched || !this.currentPublishedWorkflowId) {
      return;
    }

    try {
      const statistics = await this.workflowService.getWorkflowStatistics(
        this.currentPublishedWorkflowId,
        {
          from: new Date(this.workflow.createdAt).toISOString(),
          to: new Date().toISOString(),
          select: ['total_tasks']
        }
      );

      const countData = statistics.count || statistics;
      this.totalTasksForWorkflow = countData.total_tasks || 0;
      this.totalTasksForWorkflowFetched = true;
      this.cdr.detectChanges();
    } catch (error) {
      this.log.error('Error fetching total tasks for workflow:', error);
    }
  }

  async loadAnalyticsData(): Promise<void> {
    this.isLoadingAnalytics = true;
    this.analyticsError = null;

    try {
      this.ganttData = await this.getGanttChartData();
    } catch (error) {
      this.analyticsError = 'Failed to load analytics data';
      this.ganttData = null;
    } finally {
      this.isLoadingAnalytics = false;
    }
  }

  onGanttSegmentClick(segment: GanttSegment): void {
    const segmentToStatisticMap: Record<string, keyof GanttChartData> = {
      finished: 'finished',
      inProgress: 'inProgress',
      open: 'open',
      overdue: 'overdue',
      overdueNew: 'overdue', // Map overdueNew to overdue statistic
      scheduled: 'scheduled',
      withIncidents: 'withIncidents'
    };

    const statisticType = segmentToStatisticMap[segment.type];
    if (statisticType) {
      // Check if the statistic has data before allowing interaction
      const statisticValue = this.ganttData?.[statisticType] ?? 0;
      if (statisticValue > 0) {
        this.selectedStatistic = this.selectedStatistic === statisticType ? null : statisticType;
        // Refresh tasks with the new filter without loading state
        this.fetchTasks(0, 10);
      }
    }
  }

  onStatisticClick(statisticType: keyof GanttChartData): void {
    // Check if the statistic has data before allowing interaction
    const statisticValue = this.ganttData?.[statisticType] ?? 0;
    if (statisticValue > 0) {
      this.selectedStatistic = this.selectedStatistic === statisticType ? null : statisticType;
      // Refresh tasks with the new filter without loading state
      this.fetchTasks(0, 10);
    }
  }

  getAssignedTo(assignedTo: string[], task: TaskModel | ScheduledTaskModel): string {
    if (task.assignedToEmployeeOrEmployeegroup === TaskAssignmentType.EMPLOYEE) {
      let employeeId = assignedTo[0];
      return (
        this.employeeService.getEmployeeNameFromID(employeeId) ??
        this.employeeService.getEmployeeUserNameFromID(employeeId) ??
        employeeId
      );
    } else {
      let employeeGroups = assignedTo.map(
        (employeeGroupId) =>
          this.employeeService.getEmployeeGroupNameFromID(employeeGroupId) ?? employeeGroupId
      );
      return employeeGroups.join(', ');
    }
  }

  getDuration(
    timeTracking?: {
      name: string;
      start: Date;
      employee_id: string;
    }[]
  ) {
    if (
      !timeTracking ||
      !Array.isArray(timeTracking) ||
      timeTracking.length == 0 ||
      !timeTracking.some((checkpoint) => checkpoint.name === 'start') ||
      !timeTracking.some((checkpoint) => checkpoint.name === 'end')
    ) {
      return '-';
    }

    const startTime = new Date(
      timeTracking.find((checkpoint) => checkpoint.name === 'start')!.start
    );
    const endTime = new Date(timeTracking.find((checkpoint) => checkpoint.name === 'end')!.start);

    const deltaMillis = endTime.valueOf() - startTime.valueOf();

    if (deltaMillis > 0) {
      const deltaHours = Math.floor(deltaMillis / (1000 * 60 * 60));
      const deltaMinutes = Math.floor(deltaMillis / (1000 * 60) - deltaHours * 60);

      return `${deltaHours.toString().padStart(2, '0')}:${deltaMinutes
        .toString()
        .padStart(2, '0')}h`;
    } else {
      return '00:00h';
    }
  }

  onActionButtonClick(action: string): void {
    this.selectedTaskId = null;

    switch (action) {
      case EDIT_CALL_BACK_IDENTIFIER:
        this.workflowService.navigateToDesigner(this.currentCombinedWorkflowId);
        break;
      case CREATE_TASK_CALL_BACK_IDENTIFIER:
        this.openTaskManager();
        break;
      case DUPLICATE_CALL_BACK_IDENTIFIER:
        this.onDuplicateWorkflow();
        break;
      case PUBLISH_CALL_BACK_IDENTIFIER:
        this.onPublish();
        break;
      case UNPUBLISH_CALL_BACK_IDENTIFIER:
        this.onUnpublish();
        break;
      case DELETE_CALL_BACK_IDENTIFIER:
        this.onDelete();
        break;
      default:
        this.log.warning('Unhandled action:', action);
    }
  }

  async onDuplicateWorkflow() {
    let candidate = this.generateWorkflowCopyName(this.workflow.name.toLowerCase());

    // loop until a valid name is found
    while (true) {
      const isValid = await this.workflowService.checkWFNameIsValid(candidate).catch(() => false);
      if (isValid) {
        this.duplicatedWorkflowName = candidate;
        this.openWorkflowDialog();
        return;
      }

      // generate the next candidate and try again
      candidate = this.generateWorkflowCopyName(candidate);
    }
  }

  onTaskRowClick(task?: TaskModel): void {
    if (task) {
      this.selectedTaskId = task.taskId;
      this.workflowDesignerVisible = false;
      this.taskSidenav?.open();
      this.taskManagerSidenav?.close();
    } else {
      this.taskSidenav?.close();
      this.selectedTaskId = null;
    }
  }

  onTaskPageEvent(event: EpgTablePageEvent): void {
    if (this.currentPublishedWorkflowId) {
      this.loadingTasks = true;
      this.cdr.detectChanges();
      this.fetchTasks(event.firstIndex, event.rows);
    }
  }

  onDateFilterChange(dateFilter: DateFilterValue): void {
    if (
      this.currentDateFilter.startDate.getTime() === dateFilter.startDate.getTime() &&
      this.currentDateFilter.endDate.getTime() === dateFilter.endDate.getTime() &&
      this.currentDateFilter.preset === dateFilter.preset
    ) {
      return;
    }

    this.currentDateFilter = dateFilter;

    // Write URL query param only for custom date ranges
    if (dateFilter.preset === TaskDateFilterPresets.custom) {
      const dateFilterParam = DateRangePickerComponent.formatDateFilterParam(dateFilter, false);
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { 'date-filter': dateFilterParam },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } else {
      // Write date-filter-preset and clear date-filter for presets
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          'date-filter': null,
          'date-filter-preset': DateRangePickerComponent.formatPresetParam(dateFilter.preset)
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    this.onDateFilterInternalChange();
  }

  private onDateFilterInternalChange(): void {
    const isDetailsTabActive = this.selectedTabIndex === this.DETAILS_TAB;
    const isGalleryTabActive = this.selectedTabIndex === this.GALLERY_TAB;

    if (isDetailsTabActive) {
      if (this.currentPublishedWorkflowId) {
        this.loadingTasks = true;
        this.cdr.detectChanges();
        this.fetchTasks(0, 10);
        this.loadAnalyticsData();
      }

      // Mark gallery as stale (will re-init on next access)
      this.loadedTabs.delete(this.GALLERY_TAB);
    } else if (isGalleryTabActive) {
      this.updateGalleryFilter();

      // Mark details as stale (will re-init on next access)
      this.loadedTabs.delete(this.DETAILS_TAB);
    }
  }

  dismissSlideIn() {
    this.taskSidenav?.close();
    const resetCreationForm = this.taskManagerComponent?.resetCreationForm;
    if (typeof resetCreationForm === 'function') {
      resetCreationForm.call(this.taskManagerComponent);
    }
    this.taskManagerSidenav?.close();
    if (this.taskTable) {
      this.taskTable.rowSelection.set(null);
    }
  }

  openTaskManager(): void {
    this.selectedTaskId = null;
    this.taskSidenav?.close();
    this.taskManagerSidenav?.open();
  }

  closeTaskManager(): void {
    const resetCreationForm = this.taskManagerComponent?.resetCreationForm;
    if (typeof resetCreationForm === 'function') {
      resetCreationForm.call(this.taskManagerComponent);
    }
    this.taskManagerSidenav?.close();
  }

  @HostListener('document:keydown.escape', [])
  onKeydownHandler() {
    this.dismissSlideIn();
  }

  async canDeactivate(): Promise<boolean> {
    return this.unsavedChangesExist ? await opencanDeactivateDialog(this.dialogService) : true;
  }

  isUnsavedChangesExist(event: any): void {
    this.unsavedChangesExist = event;
  }

  formatShortDateAndTime(scheduledAt: string): string {
    return this.customDateFormatService.formatDate(scheduledAt, DateFormat.customShortDateAndTime);
  }

  formatShortDate(scheduledAt: string): string {
    return this.customDateFormatService.formatDate(scheduledAt, DateFormat.shortDate);
  }

  getRecurringConfigFromService(
    scheduledTaskRecurring: RecurringModel,
    recurringConfig: RecurringConfig,
    scheduledAt: string
  ): string {
    return this.scheduledTaskService.getRecurringConfig(
      scheduledTaskRecurring,
      recurringConfig,
      scheduledAt
    );
  }

  splitCombinedId(currentCombinedWorkflowId: string): void {
    const parts = currentCombinedWorkflowId.split('_');
    this.currentPublishedWorkflowId = parts[0] || '';
    this.currentDesignerWorkflowId = parts[1] || '';

    this.handleDesignerActionsButtons();
  }

  handleDesignerActionsButtons(): void {
    this.customButtonConfig.disabled = this.currentDesignerWorkflowId === '';
    this.actionsMenuConfig.disabled = this.currentDesignerWorkflowId === '';
    if (this.customButtonConfig2) {
      this.customButtonConfig2.disabled = this.loadingWorkflow;
    }
    let unpublishButtonConfig = this.actionsMenu.find(
      (buttonConfig) => buttonConfig.callBackIdentifier == UNPUBLISH_CALL_BACK_IDENTIFIER
    );
    if (unpublishButtonConfig) {
      unpublishButtonConfig.disabled = this.currentPublishedWorkflowId === '';
    }
    let publishButtonConfig = this.actionsMenu.find(
      (buttonConfig) => buttonConfig.callBackIdentifier == PUBLISH_CALL_BACK_IDENTIFIER
    );
    if (publishButtonConfig) {
      publishButtonConfig.disabled =
        (this.workflow?.is_published ?? false) && !(this.workflow?.is_draft ?? false);
    }
    this.cdr.detectChanges();
  }

  private updateGalleryFilter(): void {
    const newFilter: GalleryFilter = {
      from: this.currentDateFilter.startDate,
      to: this.currentDateFilter.endDate,
      workflowId: this.currentPublishedWorkflowId,
      employeeId: this.selectedEmployeeIds.length > 0 ? [...this.selectedEmployeeIds] : []
    };

    if (JSON.stringify(this.galleryFilter) !== JSON.stringify(newFilter)) {
      this.galleryLoaded = false;
      this.galleryFilter = newFilter;
    }
  }

  private applyEmployeeSelection(selectedIds: string[], origin: 'user' | 'url'): void {
    const normalizedIds = Array.from(new Set(selectedIds.filter((id) => id.length > 0)));

    this.selectedEmployeeIds = normalizedIds;

    if (origin === 'user') {
      this.syncEmployeeQueryParams(normalizedIds);
    }

    if (this.currentPublishedWorkflowId) {
      if (this.selectedTabIndex === this.DETAILS_TAB) {
        this.loadingTasks = true;
        this.cdr.detectChanges();
      }
      void this.fetchTasks(0, 10);
    }

    this.updateGalleryFilter();

    if (this.selectedTabIndex === this.DETAILS_TAB && this.currentPublishedWorkflowId) {
      this.isLoadingAnalytics = true;
      void this.loadAnalyticsData().finally(() => (this.isLoadingAnalytics = false));
    }
  }

  private syncEmployeeQueryParams(selectedIds: string[]): void {
    const queryParams =
      selectedIds.length > 0 ? { employees: selectedIds.join(',') } : { employees: null };

    this.suppressEmployeeQuerySync = true;
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .finally(() => {
        this.suppressEmployeeQuerySync = false;
      });
  }

  private extractEmployeeIds(
    selection: { id: string; type?: string }[] | string[] | string | null | undefined
  ): string[] {
    if (!selection) {
      return [];
    }

    if (Array.isArray(selection)) {
      if (selection.length === 0) {
        return [];
      }

      if (typeof selection[0] === 'string') {
        return (selection as string[]).filter((id) => typeof id === 'string' && id.length > 0);
      }

      return (selection as { id: string; type?: string }[])
        .map((employee) => employee?.id ?? '')
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
    }

    if (typeof selection === 'string') {
      const trimmed = selection.trim();
      return trimmed.length > 0 ? [trimmed] : [];
    }

    return [];
  }

  private initializeWorkflow(): WorkflowModel {
    return {
      _id: '',
      name: '',
      description: '',
      company_id: '',
      initialState: 0,
      input: {},
      output: {},
      states: [],
      updatedAt: Date.now().toString(),
      createdAt: Date.now().toString(),
      version: 0,
      combinedId: ''
    };
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    this.loadedTabs.add(event.index);

    if (event.index === this.DETAILS_TAB && this.currentPublishedWorkflowId) {
      this.loadingTasks = true;
      this.cdr.detectChanges();
      this.fetchTasks(0, 10);
      this.loadAnalyticsData();
    } else if (event.index === this.GALLERY_TAB) {
      this.updateGalleryFilter();
    }
  }

  isTabLoaded(tabIndex: number): boolean {
    return this.loadedTabs.has(tabIndex);
  }

  onEmployeeSelectionChange(
    selectedEmployees: { id: string; type?: string }[] | string[] | string | null | undefined
  ): void {
    if (!this.isEmployeeSelectionArray(selectedEmployees)) {
      return;
    }

    const selectedIds = this.extractEmployeeIds(selectedEmployees);
    this.applyEmployeeSelection(selectedIds, 'user');
  }

  private isEmployeeSelectionArray(value: unknown): value is { id: string; type?: string }[] {
    if (!Array.isArray(value)) {
      return false;
    }

    for (const employee of value as unknown[]) {
      if (!employee || typeof employee !== 'object') {
        return false;
      }

      const employeeCandidate = employee as { id?: unknown; type?: unknown };

      if (typeof employeeCandidate.id !== 'string') {
        return false;
      }

      if (employeeCandidate.type != null && typeof employeeCandidate.type !== 'string') {
        return false;
      }
    }

    return true;
  }

  private async loadAvailableEmployees(): Promise<void> {
    try {
      const employees = await this.employeeService.getEmployees();
      this.availableEmployees = employees.map((employee) => ({
        id: employee.employeeId,
        name: employee.name || employee.userName || employee.employeeId,
        type: 'employee'
      }));
    } catch (error) {
      this.log.error('Error loading employees:', error);
      this.availableEmployees = [];
    }
  }

  onGalleryImagesLoaded(hasImages: boolean): void {
    this.galleryLoaded = true;
    this.galleryHasImages = hasImages;
  }

  async checkWorkflowIsValid(): Promise<boolean> {
    const result: boolean = await this.workflowValidationService.checkWorkflowIsValid(
      this.currentDesignerWorkflowId
    );
    return result;
  }

  onFirstPublishValid() {
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      title: $localize`:label for publish workflow confirmation@@wantToPublishWfQuery:Do you want to publish this workflow?`,
      message: $localize`:label for publishing workflow to all users warning@@publishToAllUsersWarning:Publishing the workflow makes it available for assignment and execution by other employees. Published workflows can be edited and published again later on.`,
      confirmText: toTitleCase($localize`:label for publish button@@publish:Publish`),
      cancelText: toTitleCase(
        $localize`:label for leaving unpublished button@@leaveUnpublishedBtn:Leave Unpublished`
      )
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.publishWorkflow();
      }
    });
  }

  onPublishValid() {
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      title: $localize`:label for publish new version confirmation@@wantToPublishNewVersionQuery:Do you want to publish a new version?`,
      message: $localize`:label for publishing new and replacing current version warning@@publishNewVersionAndReplaceCurrentWarning:Publishing a new version replaces the current version of workflow available for assignment and execution by other employees. Published workflows can be edited and published again later on.`,
      confirmText: toTitleCase(
        $localize`:label for publish new version button@@publishNewVersionBtn:Publish New Version`
      ),
      cancelText: toTitleCase(
        $localize`:label for leaving current version published button@@leaveCurrentPublishedBtn:Leave Current Version Published`
      )
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.publishWorkflow();
      }
    });
  }

  async publishWorkflow() {
    try {
      if (this.workflow && this.workflow.designerId) {
        this.loadingWorkflow = true;
        this.actionsMenuConfig.disabled = true;
        this.cdr.detectChanges();

        this.workflow = await this.workflowService.publishWorkflow(this.workflow.designerId);

        this.router.navigate(['home', 'workflow', this.workflow.combinedId], { replaceUrl: true });
      }
    } catch (error) {
      this.log.error('Error publishing workflow:', error);
    } finally {
      this.loadingWorkflow = false;
      this.handleDesignerActionsButtons();
      this.cdr.detectChanges();
    }
  }

  async onUnpublish() {
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      template: this.workflowUnpublishTemplate,
      title: $localize`:Unpublish Workflow@@unpublishWorkflow:Unpublish Workflow`,
      message: $localize`:label for unpublish workflow details@@unpublishDetailsBefore:Once unpublished, the workflow can no longer be executed. It remains available for editing and can be published again.`,
      confirmText: toTitleCase(
        $localize`:label for unpublish button@@unpublishBtn:Unpublish And Delete Data`
      ),
      cancelText: toTitleCase($localize`:@@buttonCancel:CANCEL`)
    };

    this.dialogService.openConfirmDelete(dialogData).subscribe(async (result: boolean) => {
      if (result === true) {
        try {
          if (this.workflow && this.workflow.designerId) {
            this.loadingWorkflow = true;
            this.actionsMenuConfig.disabled = true;
            this.cdr.detectChanges();

            this.workflow = await this.workflowService.unPublishWorkflow(this.workflow.designerId);
            this.router.navigate(['home', 'workflow', this.workflow.combinedId], {
              replaceUrl: true
            });
          }
        } catch (error) {
          this.log.error('Error during unpublish operation:', error);
        } finally {
          this.loadingWorkflow = false;
          this.handleDesignerActionsButtons();
          this.cdr.detectChanges();
        }
      }
    });
  }

  async onDelete(): Promise<void> {
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      template: this.workflowUnpublishTemplate,
      title: toTitleCase($localize`:@@deleteWorkflow:Delete workflow`),
      message: $localize`:@@deleteWorkflowBefore:Once deleted, the workflow can no longer be executed. It can no longer be edited or published.`,
      confirmText: toTitleCase($localize`:@@deleteWorkflowBtn:Delete workflow and data`),
      cancelText: toTitleCase($localize`:@@buttonCancel:CANCEL`)
    };

    const dialogResult = await firstValueFrom(this.dialogService.openConfirmDelete(dialogData));
    if (dialogResult) {
      try {
        if (this.workflow && this.workflow.designerId) {
          this.loadingWorkflow = true;
          this.actionsMenuConfig.disabled = true;
          this.cdr.detectChanges();

          await this.workflowService.deleteWorkflow(
            this.workflow.designerId,
            this.workflow.is_published ?? false,
            true
          );
          this.router.navigate(['home', 'workflow'], { replaceUrl: true });
        }
      } catch (error) {
        this.log.error('Error deleting workflow:', error);
      } finally {
        this.loadingWorkflow = false;
        this.handleDesignerActionsButtons();
        this.cdr.detectChanges();
      }
    }
  }

  generateWorkflowCopyName(workflowName: string): string {
    const regex = new RegExp(`^(.*) copy(?: (\\d+))?$`);
    const match = workflowName.match(regex);

    if (match) {
      const baseName = match[1];
      const number = match[2];
      if (number) {
        const newNumber = parseInt(number, 10) + 1;
        return `${baseName} copy ${newNumber}`;
      } else {
        return `${baseName} copy 2`;
      }
    } else {
      return `${workflowName} copy`;
    }
  }

  openWorkflowDialog() {
    this.dialog.open(DialogComponent, {
      data: {
        header: $localize`:@@duplicateWorkflow:Duplicate Workflow`,
        template: this.workflowDuplicationTemplate,
        templateHandlesLayout: true,
        cancelStream: this.cancelSubject.asObservable()
      },
      width: '500px'
    });
  }

  closeWorkflowDialog() {
    this.cancelSubject.next();
  }

  async onPublish() {
    if (this.workflow) {
      if (await this.checkWorkflowIsValid()) {
        this.workflow.is_published ? this.onPublishValid() : this.onFirstPublishValid();
      } else {
        this.onPublishInvalid();
      }
    }
  }

  onPublishInvalid() {
    const dialogData: ConfirmDeleteDialogData = {
      itemName: '',
      title: $localize`:label for workflow not publishable note@@wfCannotBePublished:Workflow cannot be published yet!`,
      message: $localize`:label for invalid workflow, want to edit confirmation@@wfInvalidWantToEditQuery:The workflow you are going to publish isn't in a valid shape, yet. Do you want to edit it now to fix remaining issues?`,
      confirmText: toTitleCase($localize`:label for edit now button@@editNowBtn:Edit now`),
      cancelText: toTitleCase($localize`:label for edit later button@@editLaterBtn:No, edit later`)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.workflowService.navigateToDesigner(this.currentCombinedWorkflowId);
      }
    });
  }
}
